import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppRouter } from '@hooks/navigation';
import { useContactRouter } from '@hooks/useContactRouter';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useHasPermission } from '@hooks/useHasPermission';
import { AppRouteEnum, CallDirectionEnum, ChannelEnum, PersistedStateKeyEnum, QueryKeyEnum, TransferTypeEnum } from '@lib/enums';
import { LocaleEnum } from '@lib/enums/LocaleEnum';
import { useJotaiAtom, useJotaiValue } from '@states/jotai/hooks';
import { LocaleAtom } from '@states/localeAtom';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom, INITIAL_CURRENT_CALL_STATE } from '@states/softphone/currentCallAtom';
export type TUseCallRedirectReturn = {
  performCallRedirect: () => void;
};
export function useCallRedirect(options) {
  const $ = _c(36);
  const preferV2Routes = options?.preferV2Routes === true;
  const queryClient = useQueryClient();
  const router = useAppRouter();
  const {
    isCustomerService,
    isRetention
  } = useCurrentUser();
  const [, setLocale] = useJotaiAtom(LocaleAtom);
  const [t0, setCurrentCallState] = useJotaiAtom(currentCallAtom);
  const {
    lastTouchedQuoteId
  } = t0;
  const {
    contactId,
    contactMasterId,
    callDirection,
    channel,
    transferQuoteId,
    transferHomeQuoteId,
    transferContractId,
    transferConsumerId,
    transferType,
    isDialerReturn,
    isInternetResponder,
    isTvResponder,
    isSpanishSkillActive
  } = useJotaiValue(contactSelector);
  const {
    goToSearchPageForCurrentContact,
    goToContactPreview,
    goToExternalTransfer,
    goToQuoteFlowEntry,
    goToHomeQuoteFlowEntry,
    goToConsumerProfile,
    goToMoxyCustomerNumberSearch,
    goToContractDetails
  } = useContactRouter();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      key: "quotes:read"
    };
    t2 = {
      key: "home-quotes:read"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const [canReadQuotes, canReadHomeQuotes] = useHasPermission(t1, t2);
  let t3;
  if ($[2] !== callDirection || $[3] !== canReadHomeQuotes || $[4] !== canReadQuotes || $[5] !== channel || $[6] !== contactId || $[7] !== contactMasterId || $[8] !== goToConsumerProfile || $[9] !== goToContactPreview || $[10] !== goToContractDetails || $[11] !== goToExternalTransfer || $[12] !== goToHomeQuoteFlowEntry || $[13] !== goToMoxyCustomerNumberSearch || $[14] !== goToQuoteFlowEntry || $[15] !== goToSearchPageForCurrentContact || $[16] !== isCustomerService || $[17] !== isDialerReturn || $[18] !== isInternetResponder || $[19] !== isRetention || $[20] !== isSpanishSkillActive || $[21] !== isTvResponder || $[22] !== lastTouchedQuoteId || $[23] !== preferV2Routes || $[24] !== queryClient || $[25] !== router || $[26] !== setCurrentCallState || $[27] !== setLocale || $[28] !== transferConsumerId || $[29] !== transferContractId || $[30] !== transferHomeQuoteId || $[31] !== transferQuoteId || $[32] !== transferType) {
    t3 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Consumer],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Vehicle],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.VehicleQuote],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.HomeQuote],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.CallDetails],
        refetchType: "none"
      });
      localStorage.removeItem(PersistedStateKeyEnum.SearchType);
      localStorage.removeItem(PersistedStateKeyEnum.ManualSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchType);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.SearchMoxyCustomerNumberFormState);
      setCurrentCallState(() => ({
        ...INITIAL_CURRENT_CALL_STATE,
        lastRoutedContactId: contactId,
        lastRoutedContactMasterId: contactMasterId,
        lastTouchedQuoteId
      }));
      setLocale({
        locale: isSpanishSkillActive === true ? LocaleEnum.ES_MX : LocaleEnum.EN_US
      });
      if (transferContractId != null) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2ContractById).setId(transferContractId).go();
        } else {
          goToContractDetails(transferContractId);
        }
        return;
      }
      if (transferHomeQuoteId != null && canReadHomeQuotes) {
        if (preferV2Routes) {
          if (transferConsumerId != null) {
            router.build(AppRouteEnum.V2HomeQuoteFlowEntry).setId(transferConsumerId).setId(transferHomeQuoteId).go();
          } else {
            router.build(AppRouteEnum.V2HomeQuoteEntry).setId(transferHomeQuoteId).go();
          }
        } else {
          goToHomeQuoteFlowEntry(transferHomeQuoteId);
        }
        return;
      }
      if (transferQuoteId != null && canReadQuotes) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2QuoteFlowEntry).setId(transferQuoteId).go();
        } else {
          goToQuoteFlowEntry(transferQuoteId);
        }
        return;
      }
      if (transferConsumerId != null) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2ConsumerProfile).setId(transferConsumerId).go();
        } else {
          goToConsumerProfile(transferConsumerId);
        }
        return;
      }
      if (isCustomerService) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2Dashboard).go();
        } else {
          router.build(AppRouteEnum.Dashboard).go();
        }
        return;
      }
      if (isRetention) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2ContactSearchAuto).go();
        } else {
          goToSearchPageForCurrentContact();
        }
        return;
      }
      if (transferType != null) {
        if (preferV2Routes) {
          router.build(transferType === TransferTypeEnum.External ? AppRouteEnum.V2ContactExternalTransfer : AppRouteEnum.V2ContactSearchMoxyCustomerNumber).go();
        } else {
          transferType === TransferTypeEnum.External ? goToExternalTransfer() : goToMoxyCustomerNumberSearch();
        }
        return;
      }
      if (isDialerReturn === true) {
        router.build(preferV2Routes ? AppRouteEnum.V2ContactSearchAuto : AppRouteEnum.ContactSearchAuto).go();
        return;
      }
      if (isInternetResponder === true || isTvResponder === true) {
        router.build(preferV2Routes ? AppRouteEnum.V2ContactSearchMail : AppRouteEnum.ContactSearchMail).go();
        return;
      }
      if (callDirection === CallDirectionEnum.Outbound) {
        if (preferV2Routes) {
          router.build(AppRouteEnum.V2ContactPreview).go();
        } else {
          goToContactPreview();
        }
        return;
      }
      if (preferV2Routes) {
        router.build(channel === ChannelEnum.DirectMail && callDirection === CallDirectionEnum.Inbound ? AppRouteEnum.V2ContactSearchMail : AppRouteEnum.V2ContactSearchAuto).go();
      } else {
        goToSearchPageForCurrentContact();
      }
    };
    $[2] = callDirection;
    $[3] = canReadHomeQuotes;
    $[4] = canReadQuotes;
    $[5] = channel;
    $[6] = contactId;
    $[7] = contactMasterId;
    $[8] = goToConsumerProfile;
    $[9] = goToContactPreview;
    $[10] = goToContractDetails;
    $[11] = goToExternalTransfer;
    $[12] = goToHomeQuoteFlowEntry;
    $[13] = goToMoxyCustomerNumberSearch;
    $[14] = goToQuoteFlowEntry;
    $[15] = goToSearchPageForCurrentContact;
    $[16] = isCustomerService;
    $[17] = isDialerReturn;
    $[18] = isInternetResponder;
    $[19] = isRetention;
    $[20] = isSpanishSkillActive;
    $[21] = isTvResponder;
    $[22] = lastTouchedQuoteId;
    $[23] = preferV2Routes;
    $[24] = queryClient;
    $[25] = router;
    $[26] = setCurrentCallState;
    $[27] = setLocale;
    $[28] = transferConsumerId;
    $[29] = transferContractId;
    $[30] = transferHomeQuoteId;
    $[31] = transferQuoteId;
    $[32] = transferType;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  const performCallRedirect = t3;
  let t4;
  if ($[34] !== performCallRedirect) {
    t4 = {
      performCallRedirect
    };
    $[34] = performCallRedirect;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  return t4;
}